// Chart color palette (RET-4289).
//
// The single place JS learns chart colors. Every literal lives in custom/charts.scss
// (:root + the color-mode(dark) override); this module reads those CSS variables off
// document.documentElement, so values are ALWAYS correct for the active theme — the theme
// toggle forces a full reload, so a read at chart-build time can't go stale.
//
// Consumers: theme.js (global Chart.js defaults + the auto-color plugin) and the bespoke
// renderers (calls_receivables_and_payables_combo) and charts_js_controller chrome. Nothing here should
// hardcode a color — add it to charts.scss and read it through chartPalette().

// getComputedStyle().getPropertyValue returns the computed value with var() substituted, so
// charts.scss can reference Bootstrap brand tokens (var(--bs-primary), etc.) and JS still reads a
// resolved literal color. Fall back to a neutral when the stylesheet isn't resolved yet (first
// paint, tests) — substitution preserves leading whitespace, hence the .trim() below.
export function cssVar(name, fallback) {
    if (typeof document === 'undefined' || !document.documentElement) return fallback
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    return value || fallback
}

// Resolved palette for the active theme. Call at chart-build time (cheap; just var reads).
export function chartPalette() {
    return {
        // Data-viz series hues (theme-independent).
        cyan: cssVar('--chart-cyan', '#00e5ff'),
        green: cssVar('--chart-green', '#4ade80'),
        teal: cssVar('--chart-teal', '#14b8a6'),
        amber: cssVar('--chart-amber', '#fbbf24'),
        orange: cssVar('--chart-orange', '#d97706'),
        violet: cssVar('--chart-violet', '#8e84ff'),

        // Chrome (flips with color mode).
        ink: cssVar('--chart-ink', 'rgba(33,37,41,0.75)'), // Bootstrap body-secondary (light)
        grid: cssVar('--chart-grid', 'rgba(128,128,128,0.18)'),
        border: cssVar('--chart-border', 'rgba(128,128,128,0.18)'),
        tooltipBg: cssVar('--chart-tooltip-bg', 'rgba(10,13,16,0.96)'),

        // Calls series stroke + area-fill stops (flip with color mode).
        callsLine: cssVar('--chart-calls-line', '#0e7490'),
        areaFillTop: cssVar('--chart-area-fill-top', 'rgba(14,116,144,0.16)'),
        areaFillBottom: cssVar('--chart-area-fill-bottom', 'rgba(14,116,144,0)'),

        // Choropleth state outline (flips with color mode).
        choroplethBorder: cssVar('--chart-choropleth-border', 'rgba(125,211,230,0.35)'),

        // Neutral placeholder.
        emptyFill: cssVar('--chart-empty-fill', '#e0e0e0'),
        emptyBorder: cssVar('--chart-empty-border', '#b0b0b0'),
    }
}

// Order of brand hues for "don't care" categorical series (auto-color cycle, below).
export const CATEGORICAL = ['cyan', 'green', 'amber', 'violet', 'teal', 'orange']

// Resolve CATEGORICAL keys to the active theme's colors.
export function categoricalColors(palette) {
    return CATEGORICAL.map((key) => palette[key])
}

// Parse a CSS hex (#rgb / #rrggbb) or rgb()/rgba() string into [r, g, b] (0–255). Lets us
// derive alpha-graded variants of a themed color (e.g. the choropleth ramp) instead of
// hardcoding a second copy of the same hue. Falls back to cyan if the string is unrecognized.
function toRgb(color) {
    const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
    if (hex) {
        let h = hex[1]
        if (h.length === 3)
            h = h
                .split('')
                .map((c) => c + c)
                .join('')
        return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
    }
    const parts = color.match(/[\d.]+/g)
    return parts ? parts.slice(0, 3).map(Number) : [0, 229, 255]
}

// rgba() string for any palette color (hex or rgb/rgba) at a given alpha. Lets the bar depth
// gradient grade a single hue by opacity instead of carrying a second hardcoded color.
function toRgba(color, alpha) {
    const [r, g, b] = toRgb(color)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Choropleth fill ramp: the brand cyan whose ALPHA scales from 0 (transparent — the themed
// card background shows through, so zero/no-data states fade into the dark or light bg like
// the mockup) up to 1 (full cyan). chartjs-chart-geo calls this per feature with the value
// normalized to [0, 1]; it also drives the footer gradient legend (#renderColorScaleLegend).
export function choroplethInterpolate(palette) {
    const [r, g, b] = toRgb(palette.cyan)
    return (t) => `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, t))})`
}

// Scriptable, top→bottom CanvasGradient against the live plot area. Chart.js invokes the
// returned function every render (including resize), so the gradient tracks the real chartArea
// rather than a canvas height captured once at construction. `stops` is [[offset, color], ...]
// — colors are palette values; this helper owns only the geometry.
export function verticalGradient(stops) {
    return (context) => {
        const { ctx, chartArea } = context.chart
        if (!chartArea) return // pre-layout (first paint / tests); Chart.js retries next frame
        const grad = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        stops.forEach(([offset, color]) => grad.addColorStop(offset, color))
        return grad
    }
}

// Bar "depth" fill: the series hue fading from near-opaque at the top (lit) to translucent at
// the base (in shadow). Because verticalGradient builds the gradient in canvas space across the
// whole plot area — not per bar — a tall bar reveals more of the light top while a short one
// stays mostly in shadow, the per-bar depth in the design mockup. Pair with a solid (brighter)
// top-only border for the lit cap. Single-hue + alpha so it tracks the active theme's color.
export function barDepthFill(color) {
    return verticalGradient([
        [0, toRgba(color, 0.95)],
        [1, toRgba(color, 0.5)],
    ])
}

// Horizontal-bar (indexAxis 'y') variant of the depth fill. The gradient runs ALONG each bar's
// length (its baseline `base` → its value tip `x`) and fades ALPHA the same way the vertical bars
// do — translucent at the baseline (the card background shows through, so it lightens in light
// mode / darkens in dark mode, matching the vertical fade) brightening to the full hue at the
// tip. A longer bar travels further into the full hue, so length reads as intensity. It's built
// per bar, not across the plot area, so every row brightens on its own. Chart.js resolves this
// scriptable per element at draw time, when bar geometry is known; before that (first paint /
// tests) there's no element, so fall back to a flat fill at the lit end.
export function horizontalBarDepthFill(color) {
    return (context) => {
        const element = context.element
        if (!element || element.base == null) return toRgba(color, 0.95)
        const grad = context.chart.ctx.createLinearGradient(element.base, 0, element.x, 0)
        grad.addColorStop(0, toRgba(color, 0.4))
        grad.addColorStop(1, toRgba(color, 0.8))
        return grad
    }
}

// Stacked horizontal bars: a per-bar TOP→BOTTOM gradient across the bar's OWN thickness (its row
// height, element.y ± height/2). Stacking lays each dataset's segment end-to-end along the length,
// so the left→right length gradient (horizontalBarDepthFill) bands each segment a different
// brightness; a top→bottom one keeps every segment uniformly lit-top → shadow-bottom, so the row
// reads as one cohesive bar. Same alpha fade as the vertical bars (lit top → translucent base).
// Resolved per element at draw time; before geometry exists, fall back to a flat fill at the lit end.
export function horizontalStackedDepthFill(color) {
    return (context) => {
        const element = context.element
        if (!element || element.height == null) return toRgba(color, 0.95)
        const top = element.y - element.height / 2
        const grad = context.chart.ctx.createLinearGradient(0, top, 0, top + element.height)
        grad.addColorStop(0, toRgba(color, 0.8))
        grad.addColorStop(1, toRgba(color, 0.4))
        return grad
    }
}

// Per-side border that draws only a crisp highlight cap on a bar's top edge. A Chart.js bar
// reads a per-side borderWidth object literally (and ignores borderSkipped), so the 0 sides
// stay borderless while the lit top reads as depth against the gradient fill below it.
export const BAR_DEPTH_BORDER_WIDTH = { top: 1.5, right: 0, bottom: 0, left: 0 }

// Corner radius (px) for the rounded cap on a bar's growing end. The single source of truth for
// every bar: the auto-color plugin's stacked cap (below) and charts_js_controller's generic bar
// cap both read it, so grouped and stacked bars round identically instead of drifting 3 vs 4.
export const BAR_CAP_RADIUS = 3

// Brand replacement for Chart.js' built-in `colors` plugin. We can't use the native one: it
// has no custom-palette option AND it disables itself whenever Chart.defaults.borderColor is
// customized (theme.js does that) — see containsDefaultColorsDefenitions in chart.js. This
// colorizes only FULLY unstyled series (any explicit color wins) so the combo and other
// hand-colored charts are untouched. Arc charts (pie/doughnut/polarArea) color per slice.
export function brandAutoColorsPlugin(cycle) {
    return {
        id: 'retreaverColors',
        beforeLayout(chart) {
            if (chart.config.type === 'choropleth') return // geo owns its color scale
            chart.data.datasets.forEach((dataset, index) => {
                if (dataset.borderColor != null || dataset.backgroundColor != null) return
                const meta = chart.getDatasetMeta(index)
                const type = (meta && meta.type) || chart.config.type
                const color = cycle[index % cycle.length]
                if (type === 'pie' || type === 'doughnut' || type === 'polarArea') {
                    dataset.backgroundColor = dataset.data.map((_, i) => cycle[i % cycle.length])
                } else if (type === 'bar') {
                    // Bars get a depth gradient. Vertical bars use one plot-area gradient (a tall
                    // bar shows more lit top, a short one stays in shadow) plus a bright top-edge
                    // highlight cap. Horizontal bars (indexAxis 'y', the ranked-breakdown charts)
                    // skip that cap: unstacked bars take a left→right length gradient, while
                    // stacked bars take a top→bottom thickness gradient so each end-to-end segment
                    // stays uniformly shaded instead of banding by length.
                    const horizontal = chart.options.indexAxis === 'y'
                    const scales = chart.options.scales
                    const stacked = !!(scales?.x?.stacked || scales?.y?.stacked)
                    if (!horizontal) {
                        dataset.backgroundColor = barDepthFill(color)
                        dataset.borderWidth = BAR_DEPTH_BORDER_WIDTH
                    } else {
                        dataset.backgroundColor = stacked
                            ? horizontalStackedDepthFill(color)
                            : horizontalBarDepthFill(color)
                        dataset.borderWidth = 0
                    }
                    dataset.borderColor = color
                    // Stacked bars: round the bar's outer end (its last/outermost segment) into a
                    // soft cap. Scoped to stacked so grouped charts — where every dataset is its
                    // own bar — don't end up with only the last series rounded.
                    if (stacked && index === chart.data.datasets.length - 1) {
                        const radius = BAR_CAP_RADIUS
                        dataset.borderRadius = horizontal
                            ? { topRight: radius, bottomRight: radius, topLeft: 0, bottomLeft: 0 }
                            : { topLeft: radius, topRight: radius, bottomLeft: 0, bottomRight: 0 }
                    }
                } else {
                    dataset.borderColor = color
                    dataset.backgroundColor = color
                }
            })
        },
    }
}
